import { type RefSelectProps } from 'antd'
import clsx from 'clsx'
import { useCallback, useMemo, forwardRef, type Ref } from 'react'

import { Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { useTaxCategoriesSelectQuery } from '@/features/tax-categories/hooks'
import { type TaxCategoryEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'

// null（未設定＝上位マスタ継承）を表す番兵値。実 TaxCategory.id は正のため衝突しない。
const FOLLOW_BASE_VALUE = -1

type Props = SingleOrMultipleSelectProps<number, TaxCategoryEntity> & {
  tenantKey: string
  /** 利用可否フィルタ。既定は `true`（有効な税区分のみ表示） */
  isActive?: boolean | undefined
  /**
   * 指定すると先頭に「上位マスタ既定に従う」(value=null) を表す選択肢を追加する。
   * 荷主マスタの税区分デフォルトで null=拠点継承を明示するために使う (VOC_PJ-844 #3874)。
   * 選択すると onChange へ null を渡し、value が null/undefined のときはこの選択肢を選択表示する。
   * `mode='multiple'` との併用は想定しない（単一選択専用）。
   */
  followBaseOptionLabel?: string
  /**
   * 指定すると dcId=null（全拠点共通）または指定した dcId の税区分のみに絞り込む。
   * 拠点設定のデフォルト税区分で、表示中の拠点に関係する選択肢だけを出すために使う。
   */
  filterDcId?: number
}

/**
 * 税区分マスタを選択する Select。
 * `useQuery` で `/tax-categories?isActive=true` を fetch し `sortOrder` 順で表示する。
 * 選択時の onChange は親フォームで `taxCategoryId` への反映を想定。
 */
export const TaxCategorySelect = forwardRef(
  (
    {
      className,
      tenantKey,
      isActive = true,
      value,
      placeholder = DESCRIPTIONS.SELECT,
      mode,
      onChange,
      followBaseOptionLabel,
      filterDcId,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const { data, isFetching, isLoading } = useTaxCategoriesSelectQuery(
      tenantKey,
      { isActive }
    )

    // BE 側で sortOrder 順で返す前提だが、念のため FE 側でも保証する。
    // filterDcId 指定時は dcId=null（全拠点共通）または指定拠点のものだけに絞り込む。
    // followBaseOptionLabel 指定時は先頭に「拠点のデフォルトに従う」(null) 相当を差し込む。
    const options = useMemo(() => {
      let sorted = data
        ? [...data].sort((a, b) => a.sortOrder - b.sortOrder)
        : undefined
      if (filterDcId !== undefined && sorted) {
        sorted = sorted.filter(
          (item) => item.dcId === null || item.dcId === filterDcId
        )
      }
      if (!followBaseOptionLabel) return sorted
      const followBase = {
        id: FOLLOW_BASE_VALUE,
        name: followBaseOptionLabel
      } as TaxCategoryEntity
      return [followBase, ...(sorted ?? [])]
    }, [data, followBaseOptionLabel, filterDcId])

    const handleChange = useSelectOnChangeHandler({
      mode,
      onChange
    })

    // 番兵が選ばれたら null を返す（親フォームは null=拠点継承として扱う）
    const handleSelectChange = useCallback(
      (
        v: number | number[] | null,
        option: TaxCategoryEntity | TaxCategoryEntity[] | null
      ) => {
        if (followBaseOptionLabel && v === FOLLOW_BASE_VALUE) {
          handleChange(null, null)
          return
        }
        handleChange(v, option)
      },
      [followBaseOptionLabel, handleChange]
    )

    // value が null/undefined のときは番兵を選択表示にして「拠点のデフォルトに従う」を見せる
    const displayValue =
      followBaseOptionLabel && value == null ? FOLLOW_BASE_VALUE : value

    return (
      <Select
        customRef={ref}
        mode={mode}
        value={isLoading ? null : displayValue}
        placeholder={placeholder}
        fieldNames={{ label: 'name', value: 'id' }}
        optionFilterProp="name"
        className={clsx(['min-w-[160px]', className])}
        options={options}
        loading={isFetching}
        onChange={handleSelectChange}
        {...props}
      />
    )
  }
)
